import { createContext, useContext, useState, type ReactNode } from "react";
import type { VisaApplicationDetail } from "./visa-service";

export type AppScreen = 
  | "home" 
  | "apply-country" 
  | "apply-passport" 
  | "apply-payment" 
  | "apply-success" 
  | "status" 
  | "faq";

export type ApplicationStatus = "pending" | "approved" | "ready";

export type Application = {
  id: string;
  country: string;
  passportImage: string | null;
  status: ApplicationStatus;
  createdAt: Date;
};

type AppContextType = {
  currentScreen: AppScreen;
  setCurrentScreen: (screen: AppScreen) => void;
  passportImage: string | null;
  setPassportImage: (image: string | null) => void;
  selectedCountry: string | null;
  setSelectedCountry: (country: string | null) => void;
  applicationId: string | null;
  setApplicationId: (id: string | null) => void;
  visaType: string | null;
  setVisaType: (type: string | null) => void;
  resumeApplicationData: VisaApplicationDetail | null;
  setResumeApplicationData: (data: VisaApplicationDetail | null) => void;
  resetApplication: () => void;
};

const AppContext = createContext<AppContextType | undefined>(undefined);

export function AppProvider({ children }: { children: ReactNode }) {
  const [currentScreen, setCurrentScreen] = useState<AppScreen>("home");
  const [passportImage, setPassportImage] = useState<string | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [applicationId, setApplicationId] = useState<string | null>(null);
  const [visaType, setVisaType] = useState<string | null>(null);
  const [resumeApplicationData, setResumeApplicationData] = useState<VisaApplicationDetail | null>(null);

  const resetApplication = () => {
    setPassportImage(null);
    setSelectedCountry(null);
    setApplicationId(null);
    setVisaType(null);
    setResumeApplicationData(null);
  };

  return (
    <AppContext.Provider
      value={{
        currentScreen,
        setCurrentScreen,
        passportImage,
        setPassportImage,
        selectedCountry,
        setSelectedCountry,
        applicationId,
        setApplicationId,
        visaType,
        setVisaType,
        resumeApplicationData,
        setResumeApplicationData,
        resetApplication,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error("useApp must be used within an AppProvider");
  }
  return context;
}
